// ═══ source.io — i18n.tsx ═══
// Translation framework: TranslationProvider, useTranslation, <T>.
// Extracted from App.tsx section 8.

import React, { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { API_BASE, AppSettings } from './lib';

// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ▓▓▓▓▓ i18n FRAMEWORK ▓▓▓▓▓
// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// Strings are stored on the server (translations table) keyed by string id, e.g.
// 'common.save' or 'landing.tagline'. The frontend fetches a flat key→value map
// for the active language at mount time and exposes it through a React context.
// Components call `t('common.save', 'Save')` to look up a string with an optional
// English fallback that's used until the network request completes (so the UI
// never shows raw keys to users).
export const SUPPORTED_LANGUAGES = ['en', 'sv', 'es', 'fr', 'de', 'pt'] as const;
export type LangCode = typeof SUPPORTED_LANGUAGES[number];
export const LANGUAGE_LABELS: Record<LangCode, string> = {
  en: 'English', sv: 'Svenska', es: 'Español', fr: 'Français', de: 'Deutsch', pt: 'Português',
};

export interface TranslationContextValue {
  /** Look up `key`; if missing or untranslated, return `fallback` (or `key` itself). */
  t: (key: string, fallback?: string) => string;
  /** Currently active language code. */
  lang: LangCode;
  /** Switch language. Triggers a re-fetch of translations for the new language. */
  setLanguage: (lang: LangCode) => void;
  /** All supported languages with display labels, for pickers. */
  languages: readonly { code: LangCode; label: string }[];
}

export const TranslationContext = React.createContext<TranslationContextValue>({
  t: (_, fallback) => fallback || '',
  lang: 'en',
  setLanguage: () => {},
  languages: SUPPORTED_LANGUAGES.map(code => ({ code, label: LANGUAGE_LABELS[code] })),
});

export const TranslationProvider: React.FC<{ initialLang?: LangCode; onLanguageChange?: (lang: LangCode) => void; children: React.ReactNode }>
  = ({ initialLang = 'en', onLanguageChange, children }) => {
  const [lang, setLang] = useState<LangCode>(initialLang);
  const [translations, setTranslations] = useState<Record<string, string>>({});

  // Reload translations whenever the active language changes. The English bundle
  // is cached implicitly because the server falls back to English for missing
  // keys — we don't need to pre-load a separate English copy on the client.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/translations/${lang}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d || !d.translations) return;
        setTranslations(d.translations);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  // Sync external lang changes (e.g. user picks a different one in Settings)
  // and notify the host so it can persist the choice in AppSettings.
  // setLang is a no-op if the value is identical, so we don't need to compare.
  useEffect(() => { setLang(initialLang); }, [initialLang]);

  const setLanguage = useCallback((next: LangCode) => {
    setLang(next);
    if (onLanguageChange) onLanguageChange(next);
  }, [onLanguageChange]);

  const t = useCallback((key: string, fallback?: string): string => {
    const v = translations[key];
    if (v && v.trim()) return v;
    return fallback != null ? fallback : key;
  }, [translations]);

  const value = useMemo<TranslationContextValue>(() => ({
    t,
    lang,
    setLanguage,
    languages: SUPPORTED_LANGUAGES.map(code => ({ code, label: LANGUAGE_LABELS[code] })),
  }), [t, lang, setLanguage]);

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

export const useTranslation = () => React.useContext(TranslationContext);

// Inline translation helper. Use as `<T k="common.save" fb="Save" />` in JSX where
// adding `useTranslation()` isn't convenient (e.g. rendering inside another component's
// body where the hook would have to be threaded through props). The lookup happens
// against the active TranslationContext at render time.
export const T: React.FC<{ k: string; fb?: string }> = ({ k, fb }) => {
  const { t } = useTranslation();
  return <>{t(k, fb)}</>;
};
